import { Action, createReducer, on } from '@ngrx/store';
import Utils from 'src/app/shared/utils';

import {
  BenchmarkTypes,
  DataViewType,
  DefaultKPIselection,
  FarmerInsightsModuleState,
  FlockDataParameter,
  LocalFilterSettings,
  QueryableFlockData,
  ViewMode,
} from '../../models/farmer-insights.model';
import * as FarmerInsightsActions from '../actions/farmer-insights.actions';
import * as CycleActions from './../../../cycle/store/actions/cycle.actions';

export const initialFarmerInsightsState: FarmerInsightsModuleState = <FarmerInsightsModuleState>{
  timeSeries: null,
  benchmarkTimeSeries: null,
  benchmarkLastWeeks: null,
  benchmarkSelectedPeriodData: null,
  benchmarkLastWeeksData: null,
  benchmarkCustomerAverageSelectedPeriodData: null,
  queryableFlockData: null,
  normalizedTimeSeriesArray: [],
  isLoaded: false,
  isDetailedModeSelected: true,
  selectedDataViewType: DataViewType.PerCycle,
  selectedKpis: DefaultKPIselection,
  selectedViewMode: ViewMode.Table,
  selectedBarnIds: [],
  unselectedCycleIds: [],
  selectedLocationIds: [],
  selectedDateRange: {
    startDate: new Date(
      new Date(new Date().setFullYear(new Date().getFullYear() - 1)).setDate(
        new Date(new Date().setFullYear(new Date().getFullYear() - 1)).getDate() + 1,
      ),
    ).toISOString(),
    endDate: Utils.toUtcDate(new Date()).toISOString(),
    description: 'daterange-picker.last-12-months',
  },
  selectedConceptId: null,
  benchmarkSettings: {
    selectedNonDetailedBenchmarkType: BenchmarkTypes.None,
    selectedPeriod: false,
    lastWeeks: false,
    customerAverageSelectedPeriod: false,
  },
  globalFilterSettings: undefined,
  localFilterSettings: undefined,
  kpiGroups: [],
};

export const farmerInsightsReducer = createReducer<FarmerInsightsModuleState>(
  initialFarmerInsightsState,
  on(CycleActions.clearCycleState, (state, _) => ({
    ...state,
    selectedBarnIds: [] as number[],
    unselectedCycleIds: [] as number[],
    selectedLocationIds: [] as number[],
    selectedConceptId: null as number,
  })),
  on(FarmerInsightsActions.loadTimeSeriesSuccess, (state, { timeSeries }) => ({
    ...state,
    timeSeries,
  })),
  on(FarmerInsightsActions.loadBenchmarkTimeSeriesSuccess, (state, { timeSeries }) => ({
    ...state,
    benchmarkTimeSeries: timeSeries,
  })),
  on(FarmerInsightsActions.loadBenchmarkLastWeeksSuccess, (state, { timeSeries }) => ({
    ...state,
    benchmarkLastWeeks: timeSeries,
  })),
  on(
    FarmerInsightsActions.loadQueryableFlockDataSuccess,
    (state, { queryableFlockData, localFilterSettings }) => ({
      ...state,
      queryableFlockData,
      selectedLocationIds:
        localFilterSettings.selectedLocationIds !== null
          ? localFilterSettings.selectedLocationIds
          : queryableFlockData.locations.map((l) => l.locationId),
      unselectedCycleIds:
        localFilterSettings.unselectedCycleIds !== null
          ? localFilterSettings.unselectedCycleIds
          : queryableFlockData.cycles.map((c) => c.cycleId),
      selectedBarnIds:
        localFilterSettings.selectedBarnIds !== null
          ? localFilterSettings.selectedBarnIds
          : queryableFlockData.barns.map((b) => b.barnId),
      selectedConceptId:
        localFilterSettings.selectedConceptId !== null
          ? localFilterSettings.selectedConceptId
          : queryableFlockData.concepts[0].conceptId,
      isLoaded: true,
    }),
  ),
  on(FarmerInsightsActions.loadkpiGroups, (state, { kpiGroups }) => ({
    ...state,
    kpiGroups,
  })),
  on(FarmerInsightsActions.loadQueryableFlockDataFailed, (state) => ({
    ...state,
    queryableFlockData: null as QueryableFlockData,
    selectedLocationIds: [] as number[],
    unselectedCycleIds: [] as number[],
    selectedBarnIds: [] as number[],
    selectedConceptId: 0,
    isLoaded: true,
  })),
  on(FarmerInsightsActions.setLocationsFilter, (state, { locationIds }) => ({
    ...state,
    selectedLocationIds: locationIds,
    localFilterSettings: {
      selectedLocationIds: locationIds,
      selectedBarnIds: state.selectedBarnIds,
      selectedConceptId: state.selectedConceptId,
      unselectedCycleIds: state.unselectedCycleIds,
      selectedDateRangeStart: state.selectedDateRange.startDate,
      selectedDateRangeEnd: state.selectedDateRange.endDate,
      selectedDateRangeDescription: state.selectedDateRange.description,
    },
  })),
  on(FarmerInsightsActions.setDateRangeFilter, (state, { startDate, endDate, description }) => ({
    ...state,
    selectedDateRange: {
      startDate,
      endDate,
      description,
    },
  })),
  on(FarmerInsightsActions.setCyclesFilter, (state, { cycleIds }) => ({
    ...state,
    unselectedCycleIds: cycleIds,
    localFilterSettings: {
      selectedLocationIds: state.selectedLocationIds,
      selectedBarnIds: state.selectedBarnIds,
      selectedConceptId: state.selectedConceptId,
      unselectedCycleIds: cycleIds,
      selectedDateRangeStart: state.selectedDateRange.startDate,
      selectedDateRangeEnd: state.selectedDateRange.endDate,
      selectedDateRangeDescription: state.selectedDateRange.description,
    },
  })),
  on(FarmerInsightsActions.setBarnsFilter, (state, { barnIds }) => ({
    ...state,
    selectedBarnIds: barnIds,
    localFilterSettings: {
      selectedLocationIds: state.selectedLocationIds,
      selectedBarnIds: barnIds,
      selectedConceptId: state.selectedConceptId,
      unselectedCycleIds: state.unselectedCycleIds,
      selectedDateRangeStart: state.selectedDateRange.startDate,
      selectedDateRangeEnd: state.selectedDateRange.endDate,
      selectedDateRangeDescription: state.selectedDateRange.description,
    },
  })),
  on(FarmerInsightsActions.setSlideInFilters, (state, { locationIds, cycleIds, barnIds }) => ({
    ...state,
    selectedLocationIds: locationIds,
    unselectedCycleIds: cycleIds,
    selectedBarnIds: barnIds,
    localFilterSettings: {
      selectedLocationIds: locationIds,
      selectedBarnIds: barnIds,
      selectedConceptId: state.selectedConceptId,
      unselectedCycleIds: cycleIds,
      selectedDateRangeStart: state.selectedDateRange.startDate,
      selectedDateRangeEnd: state.selectedDateRange.endDate,
      selectedDateRangeDescription: state.selectedDateRange.description,
    },
  })),
  on(FarmerInsightsActions.setDataFilters, (state, { locationIds, cycleIds, barnIds, KPIs }) => ({
    ...state,
    selectedLocationIds: locationIds,
    unselectedCycleIds: cycleIds,
    selectedBarnIds: barnIds,
    selectedKpis: KPIs,
    localFilterSettings: {
      selectedLocationIds: locationIds,
      unselectedCycleIds: cycleIds,
      selectedBarnIds: barnIds,
      selectedConceptId: state.selectedConceptId,
      selectedDateRangeStart: state.selectedDateRange.startDate,
      selectedDateRangeEnd: state.selectedDateRange.endDate,
      selectedDateRangeDescription: state.selectedDateRange.description,
    },
    globalFilterSettings: {
      selectedKpis: KPIs,
      selectedDataViewType: state.selectedDataViewType,
      isDetailedModeSelected: state.isDetailedModeSelected,
      selectedViewMode: state.selectedViewMode,
    },
  })),
  on(FarmerInsightsActions.setKpisFilter, (state, { KPIs }) => ({
    ...state,
    selectedKpis: KPIs,
    globalFilterSettings: {
      selectedDataViewType: state.selectedDataViewType,
      selectedKpis: KPIs,
      isDetailedModeSelected: state.isDetailedModeSelected,
      selectedViewMode: state.selectedViewMode,
    },
  })),
  on(FarmerInsightsActions.setSelectedConceptId, (state, { conceptId }) => ({
    ...state,
    selectedConceptId: conceptId,
    localFilterSettings: {
      selectedLocationIds: state.selectedLocationIds,
      selectedBarnIds: state.selectedBarnIds,
      selectedConceptId: conceptId,
      unselectedCycleIds: state.unselectedCycleIds,
      selectedDateRangeStart: state.selectedDateRange.startDate,
      selectedDateRangeEnd: state.selectedDateRange.endDate,
      selectedDateRangeDescription: state.selectedDateRange.description,
    },
  })),
  on(FarmerInsightsActions.setDataViewTypeFilter, (state, { dataViewType }) => ({
    ...state,
    selectedDataViewType: dataViewType,
    globalFilterSettings: {
      selectedDataViewType: dataViewType,
      selectedKpis: state.selectedKpis,
      isDetailedModeSelected: state.isDetailedModeSelected,
      selectedViewMode: state.selectedViewMode,
    },
  })),
  on(
    FarmerInsightsActions.setNormalizedTimeSeriesArray,
    (state, { normalizedTimeSeriesArray }) => ({
      ...state,
      normalizedTimeSeriesArray,
    }),
  ),
  on(FarmerInsightsActions.setSelectedViewMode, (state, { viewMode }) => ({
    ...state,
    selectedViewMode: viewMode,
  })),
  on(
    FarmerInsightsActions.setNormalizedBenchmarkSelectedPeriodData,
    (state, { normalizedBenchmarkData }) => ({
      ...state,
      benchmarkSelectedPeriodData: normalizedBenchmarkData,
    }),
  ),
  on(
    FarmerInsightsActions.setNormalizedBenchmarkLastWeeksData,
    (state, { normalizedBenchmarkData }) => ({
      ...state,
      benchmarkLastWeeksData: normalizedBenchmarkData,
    }),
  ),
  on(
    FarmerInsightsActions.setNormalizedBenchmarkCustomerAverageSelectedPeriodData,
    (state, { normalizedBenchmarkData }) => ({
      ...state,
      benchmarkCustomerAverageSelectedPeriodData: normalizedBenchmarkData,
    }),
  ),
  on(FarmerInsightsActions.clearFarmerInsightsState, (_) => initialFarmerInsightsState),
  on(FarmerInsightsActions.setDetailedView, (state, { isDetailedView }) => ({
    ...state,
    isDetailedModeSelected: isDetailedView,
  })),
  on(
    FarmerInsightsActions.setFarmerInsightsLocalFilterSettings,
    (state, { localFilterSettings }) => ({
      ...state,
      localFilterSettings,
    }),
  ),
  on(
    FarmerInsightsActions.setFarmerInsightsGlobalFilterSettings,
    (state, { globalFilterSettings }) => ({
      ...state,
      globalFilterSettings,
    }),
  ),
  on(FarmerInsightsActions.setFarmerInsightsBenchmarkSettings, (state, { settings }) => ({
    ...state,
    benchmarkSettings: settings,
  })),
  on(
    FarmerInsightsActions.loadFarmerInsightsLocalFilterSettingsSuccess,
    (state, { localFilterSettings }) => ({
      ...state,
      localFilterSettings,
      selectedDateRange:
        localFilterSettings?.selectedDateRangeStart !== undefined
          ? {
              startDate: localFilterSettings.selectedDateRangeStart,
              endDate: localFilterSettings.selectedDateRangeEnd,
              description: localFilterSettings.selectedDateRangeDescription,
            }
          : state.selectedDateRange,
      selectedConceptId:
        state.queryableFlockData?.concepts.findIndex(
          (c) => c.conceptId === localFilterSettings.selectedConceptId,
        ) >= 0
          ? localFilterSettings.selectedConceptId
          : state.selectedConceptId,
    }),
  ),
  on(
    FarmerInsightsActions.loadFarmerInsightsGlobalFilterSettingsSuccess,
    (state, { globalFilterSettings }) => ({
      ...state,
      globalFilterSettings,
      selectedDataViewType:
        globalFilterSettings?.selectedDataViewType ?? state.selectedDataViewType,
      selectedViewMode: globalFilterSettings?.selectedViewMode ?? state.selectedViewMode,
      isDetailedModeSelected:
        globalFilterSettings?.isDetailedModeSelected ?? state.isDetailedModeSelected,
      selectedKpis: globalFilterSettings?.selectedKpis ?? state.selectedKpis,
    }),
  ),
  on(FarmerInsightsActions.resetFarmerInsightsLocalFilterSettings, (state) => ({
    ...state,
    localFilterSettings: null as LocalFilterSettings,
    selectedDateRange: initialFarmerInsightsState.selectedDateRange,
  })),
  on(FarmerInsightsActions.resetFarmerInsightsGlobalFilterSettings, (state) => ({
    ...state,
    globalFilterSettings: {
      isDetailedModeSelected: state.isDetailedModeSelected,
      selectedViewMode: state.selectedViewMode,
      selectedKpis: [] as FlockDataParameter[],
      selectedDataViewType: null as DataViewType,
    },
  })),
);

export function reducer(state: FarmerInsightsModuleState | undefined, action: Action) {
  return farmerInsightsReducer(state, action);
}
